import * as faceapi from "face-api.js"
import { useState, useEffect, useRef } from "react"
import { loadFaceApiModels } from "@/services/faceApiManager"

export type DetectionQuality = "good" | "bad" | "pending" | "no_face"

const DETECTION_OPTIONS = new faceapi.TinyFaceDetectorOptions({
  inputSize: 320,
  scoreThreshold: 0.53,
})

const GOOD_QUALITY_SCORE = 0.7
const EXPRESSION_CONFIDENCE_THRESHOLD = 0.75
const YAW_TOLERANCE_RATIO = 1.5

const checkIsReactNativeWebView = () =>
  typeof window !== "undefined" && !!(window as any).ReactNativeWebView

export const useFaceDetection = () => {
  const videoRef = useRef<HTMLVideoElement>(null)
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [modelsLoaded, setModelsLoaded] = useState(false)
  const [detectionStatus, setDetectionStatus] = useState<DetectionQuality>("pending")
  const [errorMessage, setErrorMessage] = useState<string | null>(null)
  const [nativeCameraImage, setNativeCameraImage] = useState<string | null>(null)

  const isRNWebView = checkIsReactNativeWebView()

  // Escuta a resposta da câmera nativa enviada pelo React Native
  useEffect(() => {
    if (!isRNWebView) return

    const handleMessage = (event: MessageEvent) => {
      try {
        const data = typeof event.data === "string" ? JSON.parse(event.data) : event.data
        if (data?.type === "CAMERA_RESULT" && data.base64) {
          setNativeCameraImage(`data:image/jpeg;base64,${data.base64}`)
        }
      } catch {
        // ignora mensagens não-JSON do RN
      }
    }

    window.addEventListener("message", handleMessage)
    return () => window.removeEventListener("message", handleMessage)
  }, [isRNWebView])

  // Carrega os modelos de IA apenas no navegador
  useEffect(() => {
    if (isRNWebView) return

    const loadModels = async () => {
      try {
        await loadFaceApiModels()
        setModelsLoaded(true)
      } catch (error) {
        console.error("Falha no hook ao carregar modelos:", error)
        setErrorMessage("Não foi possível carregar a IA. Verifique sua rede e recarregue a página.")
        setDetectionStatus("bad")
      }
    }
    loadModels()
  }, [isRNWebView])

  useEffect(() => {
    if (isRNWebView || !modelsLoaded || !videoRef.current) return

    const startWebcam = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: true,
        })
        if (videoRef.current) {
          videoRef.current.srcObject = stream
        }
      } catch (err) {
        console.error("Erro ao acessar a webcam:", err)
        setErrorMessage("Não foi possível acessar a webcam.")
        setDetectionStatus("bad")
      }
    }

    startWebcam()
  }, [modelsLoaded, isRNWebView])

  useEffect(() => {
    if (isRNWebView || !videoRef.current) return

    const video = videoRef.current
    let intervalId: NodeJS.Timeout

    const handlePlay = () => {
      intervalId = setInterval(async () => {
        if (video.paused || video.ended) {
          return
        }

        const detection = await faceapi
          .detectSingleFace(video, DETECTION_OPTIONS)
          .withFaceLandmarks()
          .withFaceExpressions()

        validateDetection(detection)

        if (canvasRef.current) {
          const displaySize = {
            width: video.videoWidth,
            height: video.videoHeight,
          }
          faceapi.matchDimensions(canvasRef.current, displaySize)

          const context = canvasRef.current.getContext("2d")
          if (!context) return
          context.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)

          if (detection) {
            const resizedDetections = faceapi.resizeResults(detection, displaySize)
            faceapi.draw.drawDetections(canvasRef.current, resizedDetections)
            faceapi.draw.drawFaceLandmarks(canvasRef.current, resizedDetections)
            faceapi.draw.drawFaceExpressions(canvasRef.current, resizedDetections)
          }
        }
      }, 500)
    }

    video.addEventListener("play", handlePlay)

    return () => {
      video.removeEventListener("play", handlePlay)
      if (intervalId) {
        clearInterval(intervalId)
      }
    }
  }, [modelsLoaded, isRNWebView])

  const validateDetection = (
    detection:
      | faceapi.WithFaceExpressions<faceapi.WithFaceLandmarks<faceapi.FaceDetection>>
      | undefined
  ) => {
    if (!detection) {
      setDetectionStatus("no_face")
      setErrorMessage("Nenhum rosto detectado. Centralize seu rosto.")
      return
    }

    const { detection: face, landmarks, expressions } = detection

    if (face.score < GOOD_QUALITY_SCORE) {
      setDetectionStatus("bad")
      setErrorMessage(
        `Qualidade baixa (${Math.round(face.score * 100)}%). Tente melhor iluminação/foco.`
      )
      return
    }

    if (!landmarks || landmarks.positions.length < 68) {
      setDetectionStatus("bad")
      setErrorMessage("Rosto parcialmente coberto ou de lado. Olhe para a câmera.")
      return
    }

    if (!expressions || Object.keys(expressions).length === 0) {
      setDetectionStatus("bad")
      setErrorMessage("Não foi possível ler as expressões. Verifique a iluminação/foco.")
      return
    }

    const dominantExpressionScore = Object.values(expressions).reduce((a, b) => Math.max(a, b))

    if (dominantExpressionScore < EXPRESSION_CONFIDENCE_THRESHOLD) {
      setDetectionStatus("bad")
      setErrorMessage(
        `Foto pouco nítida ou rosto não-frontal. (Confiança da análise: ${Math.round(dominantExpressionScore * 100)}%)`
      )
      return
    }

    try {
      const nose = landmarks.positions[30]
      const leftJaw = landmarks.positions[0]
      const rightJaw = landmarks.positions[16]

      const distLeft = nose.x - leftJaw.x
      const distRight = rightJaw.x - nose.x

      if (distLeft <= 0 || distRight <= 0) {
        setDetectionStatus("bad")
        setErrorMessage("Erro ao calcular a pose. Tente novamente.")
        return
      }

      const yawRatio = distRight / distLeft

      if (yawRatio > YAW_TOLERANCE_RATIO || yawRatio < 1 / YAW_TOLERANCE_RATIO) {
        setDetectionStatus("bad")
        setErrorMessage(`Rosto está virado. Olhe diretamente para a câmera.`)
        return
      }
    } catch (e) {
      setDetectionStatus("bad")
      setErrorMessage("Não foi possível analisar a pose do rosto.")
      return
    }

    setDetectionStatus("good")
    setErrorMessage(null)
  }

  const requestNativeCamera = () => {
    ;(window as any).ReactNativeWebView?.postMessage(JSON.stringify({ type: "OPEN_CAMERA" }))
  }

  const clearNativeImage = () => setNativeCameraImage(null)

  return {
    videoRef,
    canvasRef,
    detectionStatus,
    errorMessage,
    modelsLoaded,
    isRNWebView,
    nativeCameraImage,
    requestNativeCamera,
    clearNativeImage,
  }
}
